import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/apiHelper';

import { formatPrice } from '../utils/formatHelper';
import placeholderImage from '../assets/images/placeholder.jpg';
import LoadingSpinner from '../components/LoadingSpinner';
import { showError, showSuccess, showWarning, showServerError } from '../utils/toastHelper';

function Payment() {
  const navigate = useNavigate();
  const { subscriptionPlan, setSubscriptionPlan, dispatch, resetPackage, addToPackage } = useSubscription();
  const { user, updateDeliveryInfo, updateBillingInfo } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(false);
  const [hasDeliveryInfo, setHasDeliveryInfo] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [sameAsShipping, setSameAsShipping] = useState(false);
  const [invoiceType, setInvoiceType] = useState(user?.billingInfo?.type || 'individual');
  const [customerInfo, setCustomerInfo] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
    identityNumber: user?.identityNumber || '',
    billingAddress: {
      type: user?.billingInfo?.type || 'individual',
      contactName: user?.billingInfo?.contactName || '',
      city: user?.billingInfo?.city || '',
      district: user?.billingInfo?.district || '',
      country: 'Turkey',
      address: user?.billingInfo?.address || user?.billingInfo?.openAddress || '', // Fallback to openAddress if address is missing
      zipCode: user?.billingInfo?.zipCode || '',
      taxNumber: user?.billingInfo?.taxNumber || '',
      taxOffice: user?.billingInfo?.taxOffice || '',
      companyName: user?.billingInfo?.companyName || ''
    },
    shippingAddress: {
      contactName: user?.deliveryInfo?.contactName || '',
      city: user?.deliveryInfo?.city || '',
      district: user?.deliveryInfo?.district || '',
      country: 'Turkey',
      address: user?.deliveryInfo?.address || user?.deliveryInfo?.openAddress || '', // Fallback to openAddress if address is missing
      zipCode: user?.deliveryInfo?.zipCode || ''
    }
  });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentFormContent, setPaymentFormContent] = useState(null);

  const [storedDeliveryInfo, setStoredDeliveryInfo] = useState(null);

  useEffect(() => {
    if (sameAsShipping) {
      setCustomerInfo(prev => ({
        ...prev,
        billingAddress: { ...prev.shippingAddress, type: 'individual' }
      }));
    }
  }, [sameAsShipping, customerInfo.shippingAddress]);

  useEffect(() => {
    if (invoiceType === 'individual') {
      // When individual is selected, automatically set billing address same as shipping
      setSameAsShipping(true);
    }
  }, [invoiceType]);

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          error = 'Geçerli bir e-posta adresi giriniz';
        }
        break;
      case 'phoneNumber':
        const phoneRegex = /^5[0-9]{9}$/;
        if (!phoneRegex.test(value.replace(/\D/g, ''))) {
          error = 'Geçerli bir telefon numarası giriniz';
        }
        break;
      case 'zipCode':
        const zipRegex = /^[0-9]{5}$/;
        if (!zipRegex.test(value)) {
          error = 'Posta kodu 5 haneli olmalıdır';
        }
        break;
      case 'identityNumber':
        const tcRegex = /^[1-9][0-9]{10}$/;
        if (!tcRegex.test(value)) {
          error = 'Geçerli bir TC Kimlik numarası giriniz';
        }
        break;
      default:
        if (!value.trim()) {
          error = 'Bu alan zorunludur';
        }
    }
    return error;
  };

  const handleInputChange = (e, section = null) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Format phone number
    if (name === 'phoneNumber') {
      // Remove all non-digits
      let digits = value.replace(/\D/g, '');

      // If it starts with 0, remove it
      if (digits.startsWith('0')) {
        digits = digits.substring(1);
      }

      // If it doesn't start with 5, add 5 at the beginning
      if (digits.length > 0 && !digits.startsWith('5')) {
        digits = '5' + digits;
      }

      // Limit to 10 digits
      formattedValue = digits.substring(0, 10);
    }

    if (section) {
      setCustomerInfo(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [name]: formattedValue
        }
      }));
    } else {
      setCustomerInfo(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    }

    // Validate field
    const error = validateField(name, formattedValue);
    setValidationErrors(prev => ({
      ...prev,
      [section ? `${section}.${name}` : name]: error
    }));
  };

  const handleInvoiceTypeChange = (type) => {
    setInvoiceType(type);
    if (type === 'individual') {
      setSameAsShipping(true);
      setCustomerInfo(prev => ({
        ...prev,
        billingAddress: {
          ...prev.shippingAddress,
          type: 'individual'
        }
      }));
    } else {
      setSameAsShipping(false);
      // Reset billing address when switching to corporate
      setCustomerInfo(prev => ({
        ...prev,
        billingAddress: {
          contactName: '',
          city: '',
          country: 'Turkey',
          address: '',
          zipCode: '',
          taxNumber: '',
          taxOffice: '',
          companyName: '',
          type: 'corporate'
        }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Save current delivery info to session storage before payment with user ID
      const storageKey = `lastDeliveryInfo_${user?.id}`;
      sessionStorage.setItem(storageKey, JSON.stringify({
        customerInfo,
        invoiceType,
        timestamp: new Date().getTime()
      }));

      // Teslimat bilgilerini güncelle
      updateDeliveryInfo(customerInfo.shippingAddress);
      updateBillingInfo(customerInfo.billingAddress);

      // Eğer subscriptionPlan henüz backend'de oluşturulmamışsa (Package'dan context ile geldi),
      // önce abonelik kaydını oluştur, sonra ödemeyi başlat.
      let activePlan = subscriptionPlan;

      if (subscriptionPlan && !subscriptionPlan.plan?.referenceCode) {
        // Backend'de abonelik kaydı oluştur
        const createResponse = await apiRequest('/subscriptions/create', {
          method: 'POST',
          data: subscriptionPlan
        });

        if (!createResponse.success) {
          if (createResponse.existingSubscription) {
            showWarning(createResponse.message || 'Zaten bir aboneliğiniz bulunmaktadır.');
            setTimeout(() => navigate(`/subscription/user/${user?.id}`), 2000);
            return;
          }
          showError(createResponse.message || 'Abonelik oluşturulurken bir hata oluştu');
          return;
        }

        activePlan = createResponse.subscription;
        setSubscriptionPlan(activePlan);
      }

      // Ödemeyi başlat
      const response = await apiRequest(`/subscriptions/${user?.id}/initialize-payment`, {
        method: 'POST',
        data: {
          subscription: activePlan,
          customerInfo: customerInfo
        }
      });

      if (response.success) {
        if (response.recovered && response.redirectUrl) {
          showSuccess(response.message || 'Aboneliğiniz zaten aktif durumda. Yönlendiriliyorsunuz...');
          setTimeout(() => {
            window.location.href = response.redirectUrl;
          }, 1500);
          return;
        }

        if (response.redirected && response.redirectUrl) {
          window.location.href = response.redirectUrl;
          return;
        }

        if (response.subscription?.checkoutFormContent) {
          setPaymentFormContent(response.subscription.checkoutFormContent);
          setShowPaymentModal(true);
          initializePaymentForm(response.subscription.checkoutFormContent);
        } else {
          showError('Ödeme formu oluşturulamadı');
        }
      } else {
        if (response.existingSubscription) {
          showWarning(response.message || 'Zaten aktif bir aboneliğiniz bulunmaktadır.');
          setTimeout(() => {
            navigate(`/subscription/user/${user?.id}`);
          }, 2000);
          return;
        }
        showError(response.message || 'Ödeme başlatılırken bir hata oluştu');
      }
    } catch (error) {
      console.error('Payment initialization error:', error);
      handlePaymentError(error);
    } finally {
      setLoading(false);
    }
  };

  const initializePaymentForm = (formContent) => {
    cleanupPaymentForm();

    // Modal içindeki form container'ı seç
    const formContainer = document.getElementById('iyzipay-checkout-form');
    if (!formContainer) return;

    // Form içeriğini ekle
    formContainer.innerHTML = formContent;

    // Script'leri ayır ve çalıştır
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formContent;

    const scripts = tempDiv.getElementsByTagName('script');
    Array.from(scripts).forEach(script => {
      const newScript = document.createElement('script');
      if (script.src) {
        newScript.src = script.src;
      }
      if (script.textContent) {
        newScript.textContent = script.textContent;
      }
      document.body.appendChild(newScript);
    });
  };

  const handlePaymentError = (error) => {
    if (error.apiError) {
      showServerError();
    } else {
      showError(error.message || 'Ödeme başlatılırken bir hata oluştu');
    }
  };

  const cleanupPaymentForm = () => {
    // Mevcut scriptleri temizle
    const scripts = document.querySelectorAll('script[src*="iyzipay"]');
    scripts.forEach(script => script.remove());

    // Global değişkenleri temizle
    if (window.iyziInit) window.iyziInit = undefined;
    if (window.iyziUcsInit) window.iyziUcsInit = undefined;
    if (window.iyziSubscriptionInit) window.iyziSubscriptionInit = undefined;
  };

  const handleEditProducts = () => {
    try {
      // Paketteki ürünleri kontrol et
      if (subscriptionPlan?.products?.length > 0) {
        // Mevcut ürünleri pakete ekle
        subscriptionPlan.products.forEach(product => {
          addToPackage({
            ...product,
            quantity: product.quantity || 1
          });
        });

        // Ürünler sayfasına yönlendir
        navigate('/products');
      } else {
        showError('Düzenlenecek ürün bulunamadı');
      }
    } catch (error) {
      console.error('Failed to edit products:', error);
      showError('Ürünler düzenlenirken bir hata oluştu');
    }
  };

  const checkPreviousDeliveryInfo = async () => {
    try {
      // First check session storage for failed payment info with user ID
      const storageKey = `lastDeliveryInfo_${user?.id}`;
      const sessionDeliveryInfo = sessionStorage.getItem(storageKey);
      if (sessionDeliveryInfo) {
        const parsedInfo = JSON.parse(sessionDeliveryInfo);
        const timeDiff = new Date().getTime() - parsedInfo.timestamp;
        // Only use session data if it's less than 1 hour old
        if (timeDiff < 3600000) {
          setStoredDeliveryInfo(parsedInfo);
          setHasDeliveryInfo(true);
          return;
        } else {
          // Clear old session data
          sessionStorage.removeItem(storageKey);
        }
      }

      // If no session data, try API
      const response = await apiRequest(`/subscriptions/${user?.id}/delivery-info`);
      if (response.success && response.deliveryInfo) {
        setHasDeliveryInfo(true);
        const deliveryInfo = {
          invoiceType: response.deliveryInfo.billingType === 'corporate' ? 'corporate' : 'individual',
          customerInfo: {
            fullName: user?.fullName || '',
            email: user?.email || '',
            phoneNumber: response.deliveryInfo?.phoneNumber || '',
            identityNumber: response.deliveryInfo?.identityNumber || '',
            shippingAddress: {
              ...response.deliveryInfo.shippingAddress
            },
            billingAddress: response.deliveryInfo.billingType === 'corporate' ? {
              type: 'corporate',
              companyName: response.deliveryInfo.billingInfo?.companyName || '',
              taxNumber: response.deliveryInfo.billingInfo?.taxNumber || '',
              taxOffice: response.deliveryInfo.billingInfo?.taxOffice || '',
              // Check both address and openAddress from backend response to be safe
              address: response.deliveryInfo.billingInfo?.address || response.deliveryInfo.billingInfo?.openAddress || '',
              city: response.deliveryInfo.billingInfo?.city || '',
              zipCode: response.deliveryInfo.billingInfo?.zipCode || '',
              country: 'Turkey'
            } : {
              ...response.deliveryInfo.shippingAddress,
              type: 'individual'
            }
          }
        };
        setStoredDeliveryInfo(deliveryInfo);
      }
    } catch (error) {
      console.error('Failed to fetch delivery info:', error);
      setHasDeliveryInfo(false);
    }
  };

  const loadPreviousDeliveryInfo = () => {
    if (storedDeliveryInfo) {
      setInvoiceType(storedDeliveryInfo.invoiceType);
      setCustomerInfo(storedDeliveryInfo.customerInfo);
      setSameAsShipping(storedDeliveryInfo.invoiceType === 'individual');
      showSuccess('Önceki teslimat bilgileriniz getirildi');
    } else {
      showError('Kayıtlı teslimat bilgisi bulunamadı');
    }
  };

  useEffect(() => {
    if (user?.id) {
      checkPreviousDeliveryInfo();
    }
  }, [user?.id]);

  useEffect(() => {
    // İlk yüklemede subscription ve user varsa detayları çek
    if (user?.id) {
      // Eğer subscriptionPlan zaten set edilmişse ve status'ü PENDING ise,
      // gereksiz API çağrısı yapmayalım (UserSubscription'dan geliyor)
      if (subscriptionPlan?.status === 'PENDING' && subscriptionPlan?.id) {
        setIsLoadingSubscription(false);
        return;
      }
      // Package sayfasından context ile gelen ham veri (henüz backend'de oluşturulmamış)
      if (subscriptionPlan?.products && subscriptionPlan?.interval) {
        setIsLoadingSubscription(false);
        return;
      }
      fetchSubscriptionDetails(user.id);
    } else {
      setIsLoadingSubscription(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Subscription kontrolü ayrı bir useEffect'te yapılmalı
  useEffect(() => {
    // Yükleme tamamlandıktan sonra subscription kontrolü yap
    if (!isLoadingSubscription && !subscriptionPlan) {
      navigate(`/subscription/user/${user?.id}`);
    }
  }, [isLoadingSubscription]);

  const fetchSubscriptionDetails = async (userId) => {
    try {
      setSubscriptionError(false);
      const response = await apiRequest(`/subscriptions/user/${userId}`);

      if (response.success) {
        if (response.subscription.status === 'ACTIVE') {
          // Ödeme başarılı - paketi temizle
          resetPackage();
          showWarning('Aktif bir aboneliğe tekrar ödeme yapmaya çalışıyorsunuz. Aboneliklerim sayfasına yönlendiriliyorsunuz.');
          setTimeout(() => {
            navigate(`/subscription/user/${userId}`);
          }, 3000);
        } else if (response.subscription.status === 'FAILED') {
          showWarning('Abonelik durumunuzda hata tespit edildi. Lütfen aboneliklerim sayfasından kontrol edin.');
          setTimeout(() => {
            navigate(`/subscription/user/${userId}`);
          }, 3000);
        }
        setSubscriptionPlan(response.subscription);
      } else {
        setSubscriptionError(true);
        setSubscriptionPlan(null);
        showError(response.message || 'Abonelik bilgileri alınamadı');
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscriptionError(true);
      showError(error.data.message || 'Abonelik bilgileri alınamadı');
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  // Style ekle
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .payment-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      }

      .payment-modal-content {
        background: white;
        padding: 20px;
        border-radius: 8px;
        position: relative;
        width: 100%;
        max-width: 500px;
        max-height: 90vh;
        overflow-y: auto;
      }

      .payment-modal-close {
        position: absolute;
        top: 10px;
        right: 10px;
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #666;
        z-index: 1;
      }

      .payment-modal-close:hover {
        color: #333;
      }

      #iyzipay-checkout-form {
        min-height: 400px;
      }

      @media (max-width: 576px) {
        .payment-modal-content {
          width: 95%;
          margin: 10px;
        }
      }

      .delivery-info-alert {
        background-color: #F5F9FF;
        border-radius: 12px;
        padding: 1.5rem;
        margin-bottom: 1.5rem;
      }

      .delivery-info-wrapper {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      @media (min-width: 768px) {
        .delivery-info-wrapper {
          flex-direction: row;
          align-items: center;
          gap: 2rem;
        }

        .delivery-info-content {
          flex: 10;
        }

        .delivery-info-button-wrapper {
          flex: 2;
          max-width: 140px;
        }
      }

      .delivery-info-content {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
      }

      .delivery-info-icon {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #0dcaf0;
      }

      .delivery-info-text {
        flex: 1;
      }

      .delivery-info-text h6 {
        color: #2C3E50;
        font-size: 1rem;
        font-weight: 600;
        margin: 0 0 0.25rem 0;
      }

      .delivery-info-text p {
        color: #6C757D;
        font-size: 0.875rem;
        margin: 0;
      }

      .delivery-info-button {
        background-color: #5BC0DE;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 0.75rem 1rem;
        font-size: 0.9375rem;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        width: 100%;
        white-space: nowrap;
      }

      .delivery-info-button:hover {
        background-color: #4EABC7;
      }

      @media (max-width: 767px) {
        .delivery-info-alert {
          padding: 1.25rem;
        }
        
        .delivery-info-text h6 {
          font-size: 0.9375rem;
        }
        
        .delivery-info-text p {
          font-size: 0.8125rem;
        }
        
        .delivery-info-button {
          padding: 0.625rem;
          font-size: 0.875rem;
        }
      }

      .form-section {
        animation: fadeIn 0.5s ease-in-out;
      }

      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .section-title {
        color: var(--kito-olive);
        margin-bottom: 1.5rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid var(--kito-orange);
      }

      .steps {
        position: relative;
        margin-bottom: 30px;
      }

      .steps::before {
        content: '';
        position: absolute;
        top: 20px;
        left: 0;
        right: 0;
        height: 2px;
        background: #e9ecef;
        z-index: 0;
      }

      .step {
        text-align: center;
        position: relative;
        z-index: 1;
      }

      .step-icon {
        width: 40px;
        height: 40px;
        background: white;
        border: 2px solid #e9ecef;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 10px;
        transition: all 0.3s ease;
      }

      .step.completed .step-icon {
        background: var(--kito-olive);
        border-color: var(--kito-olive);
        color: white;
      }

      .step.active .step-icon {
        background: var(--kito-orange);
        border-color: var(--kito-orange);
        color: white;
        transform: scale(1.2);
      }

      .step-text {
        font-size: 0.875rem;
        color: #6c757d;
        font-weight: 500;
      }

      .step.active .step-text {
        color: var(--kito-orange);
        font-weight: 600;
      }

      .form-label {
        font-weight: 500;
        font-size: 0.9rem;
        margin-bottom: 0.5rem;
      }

      .input-group-text {
        border: none;
        background-color: #f8f9fa;
      }

      .form-control {
        border: 1px solid #e9ecef;
        padding: 0.6rem 1rem;
      }

      .form-control:focus {
        border-color: var(--kito-orange);
        box-shadow: 0 0 0 0.25rem rgba(255, 95, 0, 0.1);
      }

      .card {
        border: none;
        border-radius: 12px;
        transition: all 0.3s ease;
      }

      .card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 16px rgba(0,0,0,0.1) !important;
      }

      .delivery-card {
        border-left: 4px solid var(--kito-orange);
      }

      .billing-card {
        border-left: 4px solid var(--kito-olive);
      }

      .invoice-type-selector label {
        transition: all 0.3s ease;
        border-radius: 8px;
        min-height: 80px;
      }

      .invoice-type-selector .btn-check:checked + label {
        background-color: var(--kito-orange);
        border-color: var(--kito-orange);
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(255, 95, 0, 0.2);
      }

      .submit-button {
        padding: 1rem 2rem;
        font-size: 1.1rem;
        font-weight: 600;
        letter-spacing: 0.5px;
        border-radius: 12px;
        transition: all 0.3s ease;
        background: linear-gradient(45deg, var(--kito-orange), #ff7e3a);
        border: none;
        position: relative;
        overflow: hidden;
      }

      .submit-button:not(:disabled) {
        box-shadow: 0 4px 15px rgba(232, 108, 58, 0.2);
      }

      .submit-button:not(:disabled):hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(232, 108, 58, 0.3);
        background: linear-gradient(45deg, #ff7e3a, var(--kito-orange));
      }

      .submit-button:not(:disabled):active {
        transform: translateY(1px);
        box-shadow: 0 2px 10px rgba(232, 108, 58, 0.2);
      }

      .submit-button:disabled {
        background: linear-gradient(45deg, #e0e0e0, #f0f0f0);
        cursor: not-allowed;
        opacity: 0.8;
      }

      .submit-button .spinner-border {
        width: 1.2rem;
        height: 1.2rem;
      }

      @media (max-width: 576px) {
        .submit-button {
          padding: 0.875rem 1.5rem;
          font-size: 1rem;
        }
      }

      .alert {
        border-radius: 8px;
      }

      .subscription-summary {
        position: sticky;
        top: 100px;
      }

      .product-list {
        max-height: 300px;
        overflow-y: auto;
      }

      .product-item {
        padding: 8px;
        border-radius: 8px;
        background-color: #f8f9fa;
        transition: all 0.3s ease;
      }

      .product-item:hover {
        background-color: #e9ecef;
      }

      .product-name {
        font-size: 0.9rem;
        font-weight: 500;
      }

      .product-thumbnail {
        border-radius: 4px;
      }

      .custom-outline-btn {
        color: #6c757d;
        border-color: #6c757d;
        transition: all 0.3s ease;
      }

      .custom-outline-btn:hover {
        color: #495057;
        background-color: #f8f9fa;
        border-color: #495057;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
      }

      .custom-outline-btn:active {
        color: #495057 !important;
        background-color: #e9ecef !important;
        border-color: #495057 !important;
        transform: translateY(0);
        box-shadow: none !important;
      }

      .custom-outline-btn:focus {
        box-shadow: 0 0 0 0.25rem rgba(108, 117, 125, 0.1);
      }

      .edit-products-btn {
        color: var(--kito-orange);
        border: 1px solid var(--kito-orange);
        padding: 2px 8px;
        font-size: 0.8rem;
        border-radius: 6px;
        transition: all 0.2s ease;
        line-height: 1.2;
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }

      .edit-products-btn i {
        font-size: 0.75rem;
      }

      .edit-products-btn:hover {
        background-color: var(--kito-orange);
        color: white;
        transform: translateY(-1px);
      }

      .edit-products-btn:active {
        transform: translateY(0);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (isLoadingSubscription) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <LoadingSpinner message="Abonelik bilgileri yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card shadow-lg border-0">
              <div className="card-body p-5 text-center">
                <i className="fas fa-exclamation-circle text-danger mb-4" style={{ fontSize: '3rem' }}></i>
                <h3 className="mb-4">Abonelik Bilgileri Alınamadı</h3>
                <p className="text-muted mb-4">
                  Abonelik bilgilerinize şu anda ulaşılamıyor. Lütfen önceki adıma dönüp tekrar deneyiniz.
                </p>
                <div className="d-grid gap-3">
                  <button
                    className="btn btn-orange"
                    onClick={() => fetchSubscriptionDetails(user?.id)}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Tekrar Dene
                  </button>
                  <button
                    className="btn btn-outline-secondary custom-outline-btn"
                    onClick={() => navigate('/subscription/package')}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Önceki Adıma Dön
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionPlan) {
    return null;
  }

  return (
    <>
      <div className="container py-5 payment-form-container">
        {/* Progress Steps */}
        <div className="row justify-content-center mb-4">
          <div className="col-md-8">
            <div className="steps d-flex justify-content-between">
              <div className="step completed">
                <div className="step-icon">
                  <i className="fas fa-box"></i>
                </div>
                <div className="step-text">Paket</div>
              </div>
              <div className="step active">
                <div className="step-icon">
                  <i className="fas fa-address-card"></i>
                </div>
                <div className="step-text">Teslimat</div>
              </div>
              <div className="step">
                <div className="step-icon">
                  <i className="fas fa-credit-card"></i>
                </div>
                <div className="step-text">Ödeme</div>
              </div>
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          {/* Subscription Summary Card */}
          <div className="col-md-4 order-md-2 mb-4 mb-md-0">
            <div className="card shadow-lg border-0 subscription-summary">
              <div className="card-body p-4">
                <h4 className="text-olive mb-4">Abonelik Özeti</h4>

                <div className="subscription-details">
                  <div className="mb-4">
                    <h6 className="text-muted mb-3">Abonelik Tipi</h6>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-sync-alt text-orange me-2"></i>
                      <span className="fw-bold">
                        {subscriptionPlan?.plan?.name || 'Undefined'}
                      </span>
                    </div>
                    <div className="text-muted small mt-2">
                      <i className="fas fa-calendar-alt me-2"></i>
                      Teslimat Periyodu: {subscriptionPlan?.plan?.interval === 'MONTHLY' ? 'Her Ay' : 'Her 2 Haftada'}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-muted mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Ürünler</span>
                        <button
                          className="edit-products-btn"
                          onClick={handleEditProducts}
                        >
                          <i className="fas fa-edit"></i>
                          Düzenle
                        </button>
                      </div>
                    </h6>
                    <div className="product-list">
                      {subscriptionPlan?.products?.map((product, index) => (
                        <div key={index} className="product-item mb-2">
                          <div className="d-flex align-items-center">
                            <img
                              src={product?.mainImage || placeholderImage}
                              alt={product?.name}
                              className="product-thumbnail me-2"
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                            />
                            <div className="flex-grow-1">
                              <div className="product-name">{product?.name || 'İsimsiz Ürün'}</div>
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="product-quantity text-muted small">
                                  {product?.quantity} adet
                                </div>
                                <div className="product-price text-orange small">
                                  {formatPrice(product?.price)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-muted mb-3">Fiyat Detayı</h6>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Ürün Fiyatı:</span>
                      <span>{formatPrice(subscriptionPlan?.paymentSummary?.total)}</span>
                    </div>
                    {subscriptionPlan?.appliedDiscountCode && (
                      <div className="d-flex justify-content-between mb-2">
                        <span>İndirim Kodu:</span>
                        <span className="text-success">
                          <i className="fas fa-tag me-1"></i>
                          {subscriptionPlan.appliedDiscountCode}
                        </span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between">
                      <span>Sonraki Ödeme:</span>
                      <span>{formatPrice(subscriptionPlan?.paymentSummary?.nextPaymentAmount)}</span>
                    </div>
                  </div>

                  <div className="alert alert-light border mb-0">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-shield-alt text-success me-2"></i>
                      <div className="small">
                        Ödemeleriniz güvenli bir şekilde işleme alınmaktadır. İstediğiniz zaman aboneliğinizi iptal edebilirsiniz.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Info Form */}
          <div className="col-md-8 order-md-1">
            <div className="card shadow-lg border-0">
              <div className="card-body p-4">
                <h2 className="text-center mb-4">Teslimat Bilgileri</h2>

                {/* Previous Delivery Info Alert */}
                {hasDeliveryInfo && (
                  <div className="delivery-info-alert">
                    <div className="delivery-info-wrapper">
                      <div className="delivery-info-content flex-grow-1">

                        <div className="delivery-info-text">
                          <h6>Kayıtlı Adres Bilgileriniz Mevcut</h6>
                          <p>Kayıtlı adres bilgileriniz mevcut, kullanılsın mı?</p>
                        </div>
                      </div>
                      <div className="delivery-info-button-wrapper">
                        <button
                          className="delivery-info-button"
                          onClick={loadPreviousDeliveryInfo}
                        >
                          <i className="fas fa-sync-alt me-2"></i>
                          <span className="d-none d-md-inline">Bilgileri Getir</span>
                          <span className="d-md-none">Getir</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Summary */}
                <div className="alert alert-light border mb-4">
                  <div className="d-flex align-items-start gap-2">
                    <i className="fas fa-info-circle text-primary mt-1"></i>
                    <div className="flex-grow-1">
                      <span className="fw-bold">Önemli:</span> Lütfen bilgilerinizi eksiksiz ve doğru bir şekilde doldurunuz.
                      Bu bilgiler fatura ve teslimat için kullanılacaktır.
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                  {/* Personal Information Section */}
                  <div className="form-section mb-4">
                    <h5 className="section-title">
                      <i className="fas fa-user me-2"></i>
                      Kişisel Bilgiler
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Ad Soyad <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="fas fa-user text-muted"></i>
                          </span>
                          <input
                            type="text"
                            className={`form-control ${validationErrors.fullName ? 'is-invalid' : ''}`}
                            name="fullName"
                            value={customerInfo.fullName}
                            onChange={handleInputChange}
                            required
                            placeholder="Ad Soyad"
                          />
                          {validationErrors.fullName && (
                            <div className="invalid-feedback">{validationErrors.fullName}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          TC Kimlik Numarası <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="fas fa-id-card text-muted"></i>
                          </span>
                          <input
                            type="text"
                            className={`form-control ${validationErrors.identityNumber ? 'is-invalid' : ''}`}
                            name="identityNumber"
                            value={customerInfo.identityNumber}
                            onChange={handleInputChange}
                            required
                            maxLength="11"
                            placeholder="TC Kimlik Numarası"
                          />
                          {validationErrors.identityNumber && (
                            <div className="invalid-feedback">{validationErrors.identityNumber}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          E-posta <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="fas fa-envelope text-muted"></i>
                          </span>
                          <input
                            type="email"
                            className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                            name="email"
                            value={customerInfo.email}
                            onChange={handleInputChange}
                            required
                          />
                          {validationErrors.email && (
                            <div className="invalid-feedback">{validationErrors.email}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Telefon <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="fas fa-phone text-muted"></i>
                          </span>
                          <input
                            type="tel"
                            className={`form-control ${validationErrors.phoneNumber ? 'is-invalid' : ''}`}
                            name="phoneNumber"
                            value={customerInfo.phoneNumber}
                            onChange={handleInputChange}
                            required
                            placeholder="5XX XXX XX XX"
                          />
                          {validationErrors.phoneNumber && (
                            <div className="invalid-feedback">{validationErrors.phoneNumber}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Address Section */}
                  <div className="form-section mb-4">
                    <div className="card delivery-card">
                      <div className="card-header bg-orange text-white">
                        <h5 className="mb-0 d-flex align-items-center">
                          <i className="fas fa-shipping-fast me-2"></i>
                          Teslimat Adresi
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-12">
                            <label className="form-label">
                              Ad Soyad <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${validationErrors['shippingAddress.contactName'] ? 'is-invalid' : ''}`}
                              name="contactName"
                              value={customerInfo.shippingAddress.contactName}
                              onChange={(e) => handleInputChange(e, 'shippingAddress')}
                              required
                            />
                            {validationErrors['shippingAddress.contactName'] && (
                              <div className="invalid-feedback">{validationErrors['shippingAddress.contactName']}</div>
                            )}
                          </div>
                          <div className="col-12">
                            <label className="form-label">
                              Açık Adres <span className="text-danger">*</span>
                            </label>
                            <textarea
                              className={`form-control ${validationErrors['shippingAddress.address'] ? 'is-invalid' : ''}`}
                              name="address"
                              value={customerInfo.shippingAddress.address}
                              onChange={(e) => handleInputChange(e, 'shippingAddress')}
                              required
                              rows="3"
                            ></textarea>
                            {validationErrors['shippingAddress.address'] && (
                              <div className="invalid-feedback">{validationErrors['shippingAddress.address']}</div>
                            )}
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">
                              Şehir <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${validationErrors['shippingAddress.city'] ? 'is-invalid' : ''}`}
                              name="city"
                              value={customerInfo.shippingAddress.city}
                              onChange={(e) => handleInputChange(e, 'shippingAddress')}
                              required
                            />
                            {validationErrors['shippingAddress.city'] && (
                              <div className="invalid-feedback">{validationErrors['shippingAddress.city']}</div>
                            )}
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">
                              İlçe <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${validationErrors['shippingAddress.district'] ? 'is-invalid' : ''}`}
                              name="district"
                              value={customerInfo.shippingAddress.district}
                              onChange={(e) => handleInputChange(e, 'shippingAddress')}
                              required
                            />
                            {validationErrors['shippingAddress.district'] && (
                              <div className="invalid-feedback">{validationErrors['shippingAddress.district']}</div>
                            )}
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">
                              Posta Kodu <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${validationErrors['shippingAddress.zipCode'] ? 'is-invalid' : ''}`}
                              name="zipCode"
                              value={customerInfo.shippingAddress.zipCode}
                              onChange={(e) => handleInputChange(e, 'shippingAddress')}
                              required
                              maxLength="5"
                              placeholder="5 haneli posta kodu"
                            />
                            {validationErrors['shippingAddress.zipCode'] && (
                              <div className="invalid-feedback">{validationErrors['shippingAddress.zipCode']}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Invoice Section */}
                  <div className="form-section mb-4">
                    <div className="card mb-4">
                      <div className="card-body">
                        <h5 className="mb-3">Fatura Bilgileri</h5>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <div className="d-flex gap-4">
                              <div className="form-check">
                                <input
                                  type="radio"
                                  className="form-check-input"
                                  id="individual"
                                  name="invoiceType"
                                  value="individual"
                                  checked={invoiceType === 'individual'}
                                  onChange={(e) => handleInvoiceTypeChange(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="individual">
                                  <i className="fas fa-user me-2"></i>
                                  Bireysel
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  type="radio"
                                  className="form-check-input"
                                  id="corporate"
                                  name="invoiceType"
                                  value="corporate"
                                  checked={invoiceType === 'corporate'}
                                  onChange={(e) => handleInvoiceTypeChange(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="corporate">
                                  <i className="fas fa-building me-2"></i>
                                  Kurumsal
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        {invoiceType === 'individual' && (
                          <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            Bireysel faturada teslimat adresi fatura adresi olarak kullanılacaktır.
                          </div>
                        )}

                        {invoiceType === 'corporate' && (
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Şirket Adı</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="companyName"
                                  value={customerInfo.billingAddress.companyName}
                                  onChange={(e) => handleInputChange(e, 'billingAddress')}
                                  required
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Vergi Numarası</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="taxNumber"
                                  value={customerInfo.billingAddress.taxNumber}
                                  onChange={(e) => handleInputChange(e, 'billingAddress')}
                                  required
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Vergi Dairesi</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="taxOffice"
                                  value={customerInfo.billingAddress.taxOffice}
                                  onChange={(e) => handleInputChange(e, 'billingAddress')}
                                  required
                                />
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="mb-3">
                                <label className="form-label">Fatura Adresi</label>
                                <textarea
                                  className="form-control"
                                  name="address"
                                  value={customerInfo.billingAddress.address}
                                  onChange={(e) => handleInputChange(e, 'billingAddress')}
                                  rows="3"
                                  required
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      {error}
                    </div>
                  )}

                  <div className="d-grid">
                    <button
                      type="submit"
                      className="btn btn-orange btn-lg submit-button"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          İşleminiz Devam Ediyor...
                        </>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center">
                          <span className="me-3">Ödemeye Geç</span>
                          <i className="fas fa-arrow-right"></i>
                        </div>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <style>
          {`
          .form-section {
            animation: fadeIn 0.5s ease-in-out;
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .section-title {
            color: var(--kito-olive);
            margin-bottom: 1.5rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid var(--kito-orange);
          }

          .steps {
            position: relative;
            margin-bottom: 30px;
          }

          .steps::before {
            content: '';
            position: absolute;
            top: 20px;
            left: 0;
            right: 0;
            height: 2px;
            background: #e9ecef;
            z-index: 0;
          }

          .step {
            text-align: center;
            position: relative;
            z-index: 1;
          }

          .step-icon {
            width: 40px;
            height: 40px;
            background: white;
            border: 2px solid #e9ecef;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 10px;
            transition: all 0.3s ease;
          }

          .step.completed .step-icon {
            background: var(--kito-olive);
            border-color: var(--kito-olive);
            color: white;
          }

          .step.active .step-icon {
            background: var(--kito-orange);
            border-color: var(--kito-orange);
            color: white;
            transform: scale(1.2);
          }

          .step-text {
            font-size: 0.875rem;
            color: #6c757d;
            font-weight: 500;
          }

          .step.active .step-text {
            color: var(--kito-orange);
            font-weight: 600;
          }

          .form-label {
            font-weight: 500;
            font-size: 0.9rem;
            margin-bottom: 0.5rem;
          }

          .input-group-text {
            border: none;
            background-color: #f8f9fa;
          }

          .form-control {
            border: 1px solid #e9ecef;
            padding: 0.6rem 1rem;
          }

          .form-control:focus {
            border-color: var(--kito-orange);
            box-shadow: 0 0 0 0.25rem rgba(255, 95, 0, 0.1);
          }

          .card {
            border: none;
            border-radius: 12px;
            transition: all 0.3s ease;
          }

          .card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 16px rgba(0,0,0,0.1) !important;
          }

          .delivery-card {
            border-left: 4px solid var(--kito-orange);
          }

          .billing-card {
            border-left: 4px solid var(--kito-olive);
          }

          .invoice-type-selector label {
            transition: all 0.3s ease;
            border-radius: 8px;
            min-height: 80px;
          }

          .invoice-type-selector .btn-check:checked + label {
            background-color: var(--kito-orange);
            border-color: var(--kito-orange);
            color: white;
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(255, 95, 0, 0.2);
          }

          .submit-button {
              padding: 1rem 2rem;
              font-size: 1.1rem;
            font-weight: 600;
            letter-spacing: 0.5px;
              border-radius: 12px;
            transition: all 0.3s ease;
              background: linear-gradient(45deg, var(--kito-orange), #ff7e3a);
              border: none;
              position: relative;
              overflow: hidden;
            }

            .submit-button:not(:disabled) {
              box-shadow: 0 4px 15px rgba(232, 108, 58, 0.2);
          }

          .submit-button:not(:disabled):hover {
            transform: translateY(-2px);
              box-shadow: 0 8px 25px rgba(232, 108, 58, 0.3);
              background: linear-gradient(45deg, #ff7e3a, var(--kito-orange));
            }

            .submit-button:not(:disabled):active {
              transform: translateY(1px);
              box-shadow: 0 2px 10px rgba(232, 108, 58, 0.2);
            }

            .submit-button:disabled {
              background: linear-gradient(45deg, #e0e0e0, #f0f0f0);
              cursor: not-allowed;
              opacity: 0.8;
            }

            .submit-button .spinner-border {
              width: 1.2rem;
              height: 1.2rem;
            }

            @media (max-width: 576px) {
              .submit-button {
                padding: 0.875rem 1.5rem;
                font-size: 1rem;
              }
          }

          .alert {
            border-radius: 8px;
          }

          .subscription-summary {
            position: sticky;
            top: 100px;
          }

          .product-list {
            max-height: 300px;
            overflow-y: auto;
          }

          .product-item {
            padding: 8px;
            border-radius: 8px;
            background-color: #f8f9fa;
            transition: all 0.3s ease;
          }

          .product-item:hover {
            background-color: #e9ecef;
          }

          .product-name {
            font-size: 0.9rem;
            font-weight: 500;
          }

          .product-thumbnail {
            border-radius: 4px;
          }

          .custom-outline-btn {
            color: #6c757d;
            border-color: #6c757d;
            transition: all 0.3s ease;
          }

          .custom-outline-btn:hover {
            color: #495057;
            background-color: #f8f9fa;
            border-color: #495057;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }

          .custom-outline-btn:active {
            color: #495057 !important;
            background-color: #e9ecef !important;
            border-color: #495057 !important;
            transform: translateY(0);
            box-shadow: none !important;
          }

          .custom-outline-btn:focus {
            box-shadow: 0 0 0 0.25rem rgba(108, 117, 125, 0.1);
          }

            .edit-products-btn {
              color: var(--kito-orange);
              border: 1px solid var(--kito-orange);
              padding: 2px 8px;
              font-size: 0.8rem;
              border-radius: 6px;
              transition: all 0.2s ease;
              line-height: 1.2;
              display: inline-flex;
              align-items: center;
              gap: 4px;
            }

            .edit-products-btn i {
              font-size: 0.75rem;
            }

            .edit-products-btn:hover {
              background-color: var(--kito-orange);
              color: white;
              transform: translateY(-1px);
            }

            .edit-products-btn:active {
              transform: translateY(0);
            }

            @media (max-width: 767px) {
              .alert {
                padding: 12px;
                margin-bottom: 16px;
              }

              .alert strong {
                font-size: 0.9rem;
                margin-left: 8px;
              }

              .alert p {
                font-size: 0.8rem;
                margin-left: 24px;
              }

              .alert .btn {
                padding: 6px 12px;
                font-size: 0.8rem;
                margin-left: 24px;
                margin-top: 8px;
              }

              .alert i.fa-lg {
                font-size: 1.2rem;
              }
          }
        `}
        </style>
      </div>
      {showPaymentModal && (
        <div className="payment-modal">
          <div className="payment-modal-content">
            <button
              className="payment-modal-close"
              onClick={() => {
                cleanupPaymentForm();
                setShowPaymentModal(false);
              }}
            >
              ×
            </button>
            <div id="iyzipay-checkout-form"></div>
          </div>
        </div>
      )}
    </>
  );
}

export default Payment; 